import { createContext, FC, ReactNode, useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { useFastPassOffer, UseFastPassOfferResult } from '../hooks/useFastPassOffer';

/**
 * FastPassOfferProvider
 *
 * Antes deste provider, `useFastPassOffer` era chamado 3 vezes durante a
 * jornada de resale (`ResalePage`, `ResalePaymentPage`, `ResaleAppBar`).
 * Cada chamada disparava um fetch independente para a Edge Function, gerando
 * 4 requisicoes por jornada (2 na landing + 2 na pagina de pagamento) mesmo
 * quando a oferta ja havia sido resolvida. Isso sobrecarregava a Edge Function
 * e permitia que cada consumidor observasse um estado de loading diferente —
 * podendo mostrar preco antigo enquanto outro ja mostrava o `finalPrice`.
 *
 * Este provider centraliza o hook:
 * - Chama `useFastPassOffer(resaleId)` UMA vez, dentro do `ResaleLayout`.
 * - Expoe o resultado via `useFastPassOfferContext()` para todos os
 *   consumidores (pagina, AppBar, summary, etc.).
 * - `resaleId` e derivado de `useLocation()` (matching `/resale/:resaleId`)
 *   para que o provider funcione tanto em `/resale/:resaleId` quanto em
 *   `/resale/:resaleId/payment` sem precisar que o consumidor passe o id.
 *
 * Consumidores devem usar `useFastPassOfferContext()` (nunca `useFastPassOffer`
 * diretamente) para evitar fetches duplicados. Chamar o contexto fora do
 * provider lanca um erro claro em dev — protege contra regressao.
 */

const FastPassOfferContext = createContext<UseFastPassOfferResult | null>(null);

interface FastPassOfferProviderProps {
  children: ReactNode;
}

const RESALE_PATH_REGEX = /^\/resale\/([^/]+)/;

export const FastPassOfferProvider: FC<FastPassOfferProviderProps> = ({ children }) => {
  const location = useLocation();
  const resaleIdMatch = location.pathname.match(RESALE_PATH_REGEX);
  const resaleId = resaleIdMatch?.[1];

  const state = useFastPassOffer(resaleId);

  return (
    <FastPassOfferContext.Provider value={state}>
      {children}
    </FastPassOfferContext.Provider>
  );
};

export const useFastPassOfferContext = (): UseFastPassOfferResult => {
  const context = useContext(FastPassOfferContext);

  if (context === null) {
    throw new Error(
      'useFastPassOfferContext must be used within a FastPassOfferProvider. '
      + 'Wrap the consumer inside <FastPassOfferProvider> (already done in ResaleLayout).'
    );
  }

  return context;
};
